/** Number of upcoming moves shown by the ghost guide assistant (or fewer if solved sooner). */
export const GHOST_GUIDE_STEP_COUNT = 10;

const MAX_SEARCH_NODES = 50000;

function layoutKey(layout) {
    return layout.map((v) => Number(v)).join(",");
}

function isSolvedLayout(layout) {
    return layout.every((v, i) => Number(v) === i);
}

function manhattanDistance(layout, gridSize, emptySliceIdx) {
    let sum = 0;
    for (let visualIdx = 0; visualIdx < layout.length; visualIdx += 1) {
        const slice = Number(layout[visualIdx]);
        if (slice === emptySliceIdx) continue;
        const goalIdx = slice;
        const r1 = Math.floor(visualIdx / gridSize);
        const c1 = visualIdx % gridSize;
        const r2 = Math.floor(goalIdx / gridSize);
        const c2 = goalIdx % gridSize;
        sum += Math.abs(r1 - r2) + Math.abs(c1 - c2);
    }
    return sum;
}

function getNeighborMoves(layout, gridSize, emptySliceIdx) {
    const emptyVisual = layout.findIndex((v) => Number(v) === emptySliceIdx);
    if (emptyVisual < 0) return [];

    const er = Math.floor(emptyVisual / gridSize);
    const ec = emptyVisual % gridSize;
    const deltas = [
        [-1, 0],
        [1, 0],
        [0, -1],
        [0, 1],
    ];
    const moves = [];

    deltas.forEach(([dr, dc]) => {
        const nr = er + dr;
        const nc = ec + dc;
        if (nr < 0 || nr >= gridSize || nc < 0 || nc >= gridSize) return;
        const visualIdx = nr * gridSize + nc;
        const next = [...layout];
        [next[visualIdx], next[emptyVisual]] = [next[emptyVisual], next[visualIdx]];
        moves.push({
            layout: next,
            sliceIndex: Number(layout[visualIdx]),
        });
    });

    return moves;
}

/**
 * Returns the next {@link maxSteps} slice indices to tap (does not mutate the board).
 * Uses A* toward the solved layout.
 *
 * @param {number[]} layout row-major permutation; layout[visualIdx] = slice index
 * @param {number} gridSize
 * @param {number} [emptySliceIdx] slice rendered as the empty hole (default n−1)
 * @param {number} [maxSteps]
 * @returns {number[]}
 */
export function computeGhostGuideSteps(
    layout,
    gridSize,
    emptySliceIdx = null,
    maxSteps = GHOST_GUIDE_STEP_COUNT,
) {
    const gs = Number(gridSize);
    const n = gs * gs;
    if (!Array.isArray(layout) || layout.length !== n || gs < 2) {
        return [];
    }

    const normalized = layout.map((v) => Number(v));
    const emptyIdx =
        emptySliceIdx != null && Number.isFinite(Number(emptySliceIdx))
            ? Number(emptySliceIdx)
            : n - 1;

    if (isSolvedLayout(normalized)) {
        return [];
    }

    const open = [
        {
            layout: normalized,
            path: [],
            f: manhattanDistance(normalized, gs, emptyIdx),
        },
    ];
    const gScore = new Map([[layoutKey(normalized), 0]]);
    const closed = new Set();

    let explored = 0;

    while (open.length > 0 && explored < MAX_SEARCH_NODES) {
        open.sort((a, b) => a.f - b.f);
        const current = open.shift();
        explored += 1;
        const currentKey = layoutKey(current.layout);

        if (closed.has(currentKey)) continue;
        closed.add(currentKey);

        if (isSolvedLayout(current.layout)) {
            return current.path.slice(0, maxSteps);
        }

        if (current.path.length >= maxSteps) {
            return current.path;
        }

        const baseG = gScore.get(currentKey) ?? 0;

        getNeighborMoves(current.layout, gs, emptyIdx).forEach(({ layout: nextLayout, sliceIndex }) => {
            const nextKey = layoutKey(nextLayout);
            if (closed.has(nextKey)) return;

            const tentativeG = baseG + 1;
            const knownG = gScore.get(nextKey);
            if (knownG != null && tentativeG >= knownG) return;

            gScore.set(nextKey, tentativeG);
            open.push({
                layout: nextLayout,
                path: [...current.path, sliceIndex],
                f: tentativeG + manhattanDistance(nextLayout, gs, emptyIdx),
            });
        });
    }

    return [];
}

const IDA_FOUND = -1;

/**
 * Full IDA* path as successive board layouts after each slide (excludes start).
 * Same algorithm as Android `hintPath` / `idaHintPath` — fast enough for 4×4 HARD recording.
 *
 * @param {number[]} layout
 * @param {number} gridSize
 * @param {number|null} [emptySliceIdx]
 * @returns {number[][]}
 */
export function computeSolveLayouts(layout, gridSize, emptySliceIdx = null) {
    const gs = Number(gridSize);
    const cellCount = gs * gs;
    if (!Array.isArray(layout) || layout.length !== cellCount || gs < 2) {
        return [];
    }

    const start = layout.map((v) => Number(v));
    const emptyIdx =
        emptySliceIdx != null && Number.isFinite(Number(emptySliceIdx))
            ? Number(emptySliceIdx)
            : cellCount - 1;

    if (isSolvedLayout(start)) {
        return [];
    }

    const path = [start];
    const onPath = new Set([layoutKey(start)]);
    let nodes = 0;
    const maxNodes = gs <= 4 ? 250000 : 80000;
    const maxBound = gs <= 4 ? 80 : 36;

    function search(g, bound) {
        const current = path[path.length - 1];
        const h = manhattanDistance(current, gs, emptyIdx);
        const f = g + h;
        if (f > bound) return f;
        if (h === 0) return IDA_FOUND;
        if (nodes > maxNodes) return Infinity;

        let min = Infinity;
        const emptyVisual = current.findIndex((value) => Number(value) === emptyIdx);
        if (emptyVisual < 0) return Infinity;

        const deltas = [
            [-1, 0],
            [1, 0],
            [0, -1],
            [0, 1],
        ];
        for (let i = 0; i < deltas.length; i += 1) {
            const [dr, dc] = deltas[i];
            const er = Math.floor(emptyVisual / gs);
            const ec = emptyVisual % gs;
            const nr = er + dr;
            const nc = ec + dc;
            if (nr < 0 || nr >= gs || nc < 0 || nc >= gs) continue;
            const visualIdx = nr * gs + nc;
            const next = [...current];
            [next[visualIdx], next[emptyVisual]] = [next[emptyVisual], next[visualIdx]];
            const key = layoutKey(next);
            if (onPath.has(key)) continue;
            nodes += 1;
            path.push(next);
            onPath.add(key);
            const t = search(g + 1, bound);
            if (t === IDA_FOUND) return IDA_FOUND;
            if (t < min) min = t;
            path.pop();
            onPath.delete(key);
        }
        return min;
    }

    let bound = manhattanDistance(start, gs, emptyIdx);
    while (bound <= maxBound) {
        nodes = 0;
        const t = search(0, bound);
        if (t === IDA_FOUND) {
            return path.slice(1);
        }
        if (!Number.isFinite(t)) break;
        bound = t;
    }
    return [];
}
